/**
 * @file UnsavedGuard.tsx
 * @description Защита несохранённых правок черновика (phase19.16 Р1;
 * uiux-design-r1 §1 «Черновик не сохранился»: «Есть несохранённые изменения.
 * Уйти и потерять их?» [Остаться] [Уйти]).
 *
 * Срабатывает, только когда правки не дошли до сервера (ошибка связи или
 * конфликт): обычные правки сохраняются сами через секунду. Переход внутри
 * кабинета — окно кабинета (`useBlocker`), закрытие вкладки — окно
 * браузера (`beforeunload`, текст в нём задаёт браузер). `blockSearch` —
 * держать и смену параметров адреса (выбор в конструкторе сайта, «назад»).
 */

import { useEffect } from "react";
import { useBlocker } from "react-router";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";

interface Props {
  when: boolean;
  /**
   * Держать и переходы внутри экрана (другие параметры адреса: страница,
   * раздел, блок конструктора) — когда правки живут только в этой панели.
   */
  blockSearch?: boolean;
  /** Переход уже разрешён своим окном (например, «Сохранить / Не сохранять»). */
  skip?: () => boolean;
}

export function UnsavedGuard({ when, blockSearch = false, skip }: Props) {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!when || skip?.()) return false;
    if (currentLocation.pathname !== nextLocation.pathname) return true;
    return blockSearch && currentLocation.search !== nextLocation.search;
  });

  useEffect(() => {
    if (!when) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [when]);

  const open = blocker.state === "blocked";
  return (
    <AlertDialog open={open} onOpenChange={(next) => (!next && open ? blocker.reset?.() : null)}>
      <AlertDialogContent fullScreen className="max-w-md gap-3">
        <AlertDialogHeader>
          <AlertDialogTitle>Есть несохранённые изменения</AlertDialogTitle>
          <AlertDialogDescription>Уйти и потерять их? Они есть только в этом окне.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="ghost" onClick={() => blocker.proceed?.()}>
            Уйти
          </Button>
          <Button onClick={() => blocker.reset?.()}>Остаться</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
